import { type ActionTypes, Machine, type StateMachine, assign } from "xstate";

export interface StateSchema {
    states: {
        valid: NonNullable<unknown>;
        error: NonNullable<unknown>;
    };
}

interface ChangedEvent<InputType> {
    type: "CHANGED";
    newValue: InputType;
}

interface ResetEvent {
    type: "RESET";
}

interface XstateInitEvent {
    type: ActionTypes.Init;
}

export type Events<InputType> = ChangedEvent<InputType> | ResetEvent | XstateInitEvent;

export interface Context<InputType> {
    value: InputType;
    isTouched: boolean;
}

export const createInputStateMachine = <InputType>(
    defaultValue: InputType,
    validator: (value: InputType) => boolean,
): StateMachine<Context<InputType>, StateSchema, Events<InputType>> => {
    return Machine<Context<InputType>, StateSchema, Events<InputType>>(
        {
            id: "inputStateMachine",
            initial: validator(defaultValue) ? "valid" : "error",
            context: {
                value: defaultValue,
                isTouched: false,
            },
            states: {
                valid: {
                    on: {
                        CHANGED: [
                            { target: "valid", cond: "isValid", actions: ["setNewValue"] },
                            { target: "error", actions: ["setNewValue"] },
                        ],
                        RESET: [
                            { target: "valid", cond: "isDefaultValid", actions: ["reset"] },
                            { target: "error", actions: ["reset"] },
                        ],
                    },
                },
                error: {
                    on: {
                        CHANGED: [
                            { target: "valid", cond: "isValid", actions: ["setNewValue"] },
                            { target: "error", actions: ["setNewValue"] },
                        ],
                        RESET: [
                            {
                                target: "valid",
                                cond: "isDefaultValid",
                                actions: ["reset"],
                            },
                            { target: "error", actions: ["reset"] },
                        ],
                    },
                },
            },
        },
        {
            guards: {
                isValid: (_, event) => {
                    if (event.type === "CHANGED") {
                        return validator(event.newValue);
                    }
                    return false;
                },
                isDefaultValid: () => validator(defaultValue),
            },
            actions: {
                reset: assign<Context<InputType>, Events<InputType>>({
                    value: defaultValue,
                    isTouched: false,
                }),
                setNewValue: assign<Context<InputType>, Events<InputType>>({
                    value: (_, event) => (event.type === "CHANGED" ? event.newValue : defaultValue),
                    isTouched: true,
                }),
            },
        },
    );
};
